import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  CredentialType,
  CredentialEntry,
  encryptCredential,
  encryptWithPublicKey,
  validatePasswordStrength
} from "../../utils/crypto/vault-crypto";
import { bulkImportCredentials, BulkImportEntryData } from "../../utils/api/endpoints/vault";
import axios from "../../utils/api/axios";
import { FiPlus, FiTrash2, FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import {
  credentialTypes,
  entryTypes,
  getDefaultEntries,
  getLabelPlaceholder
} from "./credential-utils";

interface Target {
  target_id: string;
  target_name: string;
  target_value: string;
  asset?: {
    name: string;
  };
}

interface MultiCredentialModalProps {
  isOpen: boolean;
  vaultId: number;
  vaultName: string;
  onClose: () => void;
  onImportComplete: () => void;
  masterPassword?: string;
  publicKey?: string;
}

interface NewCredentialRow {
  id: string;
  label: string;
  credential_type: CredentialType;
  entries: CredentialEntry[];
  url: string; // Top level target URL
  notes: string;
  is_favorite: boolean;
}


const MultiCredentialModal: React.FC<MultiCredentialModalProps> = ({
  isOpen,
  vaultId,
  vaultName,
  onClose,
  onImportComplete,
  masterPassword: initialMasterPassword,
  publicKey
}) => {
  const isSubUserMode = !initialMasterPassword && !!publicKey;
  const [rows, setRows] = useState<NewCredentialRow[]>([]);
  const [masterPassword, setMasterPassword] = useState(initialMasterPassword || "");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showValues, setShowValues] = useState<Record<string, boolean>>({});
  const [companyTargets, setCompanyTargets] = useState<Target[]>([]);
  const [showTargetDropdown, setShowTargetDropdown] = useState<Record<string, boolean>>({});
  const [isSearchingTargets, setIsSearchingTargets] = useState(false);

  const fetchCompanyTargets = async () => {
    setIsSearchingTargets(true);
    try {
      const response = await axios.get("/assets/all-targets");
      setCompanyTargets(response.data);
    } catch (err) {
      console.error("Failed to fetch targets:", err);
    } finally {
      setIsSearchingTargets(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setRows([createEmptyRow()]);
      setError(null);
      if (initialMasterPassword) setMasterPassword(initialMasterPassword);
      fetchCompanyTargets();
    }
  }, [isOpen, initialMasterPassword]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
       Object.keys(showTargetDropdown).forEach(id => {
         if (showTargetDropdown[id]) {
           const targetInput = document.querySelector(`[data-target-dropdown="${id}"]`);
           if (targetInput && !targetInput.contains(e.target as Node)) {
             setShowTargetDropdown(prev => ({ ...prev, [id]: false }));
           }
         }
       });
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, showTargetDropdown]);

  const createEmptyRow = (type: CredentialType = "password"): NewCredentialRow => ({
    id: Math.random().toString(36).substring(2, 9),
    label: "",
    credential_type: type,
    entries: getDefaultEntries(type),
    url: "",
    notes: "",
    is_favorite: false
  });

  const addRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  const removeRow = (id: string) => {
    if (rows.length > 1) {
      setRows(rows.filter(row => row.id !== id));
    }
  };

  const updateRowField = (id: string, field: keyof NewCredentialRow, value: any) => {
    setRows(rows.map(row => {
      if (row.id === id) {
        if (field === "credential_type") {
           return { ...row, credential_type: value as CredentialType, entries: getDefaultEntries(value as CredentialType) };
        }
        return { ...row, [field]: value };
      }
      return row;
    }));
  };

  const updateRowEntry = (rowId: string, entryIndex: number, field: keyof CredentialEntry, value: string) => {
    setRows(rows.map(row => {
      if (row.id === rowId) {
        const newEntries = [...row.entries];
        newEntries[entryIndex] = { ...newEntries[entryIndex], [field]: value };
        return { ...row, entries: newEntries };
      }
      return row;
    }));
  };

  const addRowEntry = (rowId: string) => {
    setRows(rows.map(row => {
      if (row.id === rowId) {
        return { ...row, entries: [...row.entries, { key: "", value: "", type: "text" as const }] };
      }
      return row;
    }));
  };

  const removeRowEntry = (rowId: string, entryIndex: number) => {
    setRows(rows.map(row => {
      if (row.id === rowId && row.entries.length > 1) {
        return { ...row, entries: row.entries.filter((_, i: number) => i !== entryIndex) };
      }
      return row;
    }));
  };


  const toggleShowValue = (id: string) => {
    setShowValues(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSaveAll = async () => {
    setError(null);

    // Filter out rows without a label
    const filledRows = rows.filter(row => row.label.trim());

    if (filledRows.length === 0) {
      setError("Please ensure all rows have labels entered.");
      return;
    }

    // Check if any row lacks a primary "password" type secret, unless it's a secure note
    const invalidRowIndex = filledRows.findIndex(row => 
      !row.label.trim() || 
      (row.credential_type !== "secure_note" && !row.entries.some(e => e.type === "password" && e.value.trim()))
    );

    if (invalidRowIndex >= 0) {
      setError(`Row ${invalidRowIndex + 1}: Label and at least one Secret/Password are required.`);
      return;
    }

    if (!isSubUserMode && !masterPassword) {
      setError("Master password is required for encryption.");
      return;
    }

    if (!isSubUserMode) {
      const validation = validatePasswordStrength(masterPassword);
      if (!validation.isValid) {
        setError(`Master password is too weak: ${validation.feedback.join(", ")}`);
        return;
      }
    }

    setIsLoading(true);

    try {
      const encryptedEntries: BulkImportEntryData[] = [];

      for (const row of filledRows) {
        // Extract username for quick indexing if present in the entries
        const usernameEntry = row.entries.find(e => e.type === "username" || e.key.toLowerCase() === "username");
        const topLevelUsername = usernameEntry ? usernameEntry.value.trim() : undefined;

        let encryptedData;

        if (isSubUserMode && publicKey) {
          encryptedData = await encryptWithPublicKey({ entries: row.entries }, publicKey);
          encryptedEntries.push({
            label: row.label.trim(),
            credential_type: row.credential_type,
            url: row.url.trim() || undefined,
            username: topLevelUsername,
            encrypted_data: encryptedData.encrypted_data,
            iv: encryptedData.iv,
            auth_tag: encryptedData.auth_tag,
            salt: "", // API handles null conversion
            encrypted_credential_key: encryptedData.encrypted_aes_key,
            is_favorite: row.is_favorite
          });
        } else {
          encryptedData = await encryptCredential({ entries: row.entries }, masterPassword);
          encryptedEntries.push({
            label: row.label.trim(),
            credential_type: row.credential_type,
            url: row.url.trim() || undefined,
            username: topLevelUsername,
            encrypted_data: encryptedData.encrypted_data,
            iv: encryptedData.iv,
            auth_tag: encryptedData.auth_tag,
            salt: encryptedData.salt,
            key_iterations: encryptedData.key_iterations,
            is_favorite: row.is_favorite
          });
        }
      }

      await bulkImportCredentials(vaultId, encryptedEntries);
      onImportComplete();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to save credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
        {/* Header */}
        <div className="px-6 py-4 bg-slate-800 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-700 rounded-lg">
              <FiPlus className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Bulk Add Credentials</h2>
              <p className="text-slate-400 text-sm">Vault: {vaultName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-white transition-colors">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
              {error}
            </div>
          )}

          <div className="flex flex-col gap-4">
            {rows.map((row, index) => {
              
              return (
                <div key={row.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 relative group transition-all hover:border-slate-300">
                  {/* Row Header & Delete */}
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                       <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs font-bold">
                         {index + 1}
                       </span>
                       <h4 className="text-sm font-semibold text-slate-700">Credential Details</h4>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 cursor-pointer group/fav">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                          checked={row.is_favorite}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRowField(row.id, "is_favorite", e.target.checked)}
                        />
                        <span className="text-xs text-slate-500 group-hover/fav:text-slate-800 transition-colors">Favourite ⭐</span>
                      </label>
                      <button
                        onClick={() => removeRow(row.id)}
                        disabled={rows.length === 1}
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                        title="Remove Credential"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Grid Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {/* Type Select */}
                    <div className="col-span-1 md:col-span-3">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">Type</label>
                      <select
                        className="w-full bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:bg-white outline-none transition-colors"
                        value={row.credential_type}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateRowField(row.id, "credential_type", e.target.value)}
                      >
                        {credentialTypes.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Label Input */}
                    <div className="col-span-1 md:col-span-5">
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Label (Name) *</label>
                      <input
                        type="text"
                        placeholder={getLabelPlaceholder(row.credential_type)}
                        className="w-full bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:bg-white outline-none transition-colors"
                        value={row.label}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRowField(row.id, "label", e.target.value)}
                      />
                    </div>

                    {/* Target URL Input */}
                    {row.credential_type !== "secure_note" && (
                      <div className="col-span-1 md:col-span-4" data-target-dropdown={row.id}>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">URL / Service Target</label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="https://... or select Target"
                            className="w-full bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:bg-white outline-none transition-colors"
                            value={row.url}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              updateRowField(row.id, "url", e.target.value);
                              setShowTargetDropdown(prev => ({ ...prev, [row.id]: true }));
                            }}
                            onFocus={() => setShowTargetDropdown(prev => ({ ...prev, [row.id]: true }))}
                          />
                          {showTargetDropdown[row.id] && companyTargets.length > 0 && (
                            <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                              {companyTargets
                                .filter(t => 
                                  t.target_name.toLowerCase().includes(row.url.toLowerCase()) || 
                                  t.target_value.toLowerCase().includes(row.url.toLowerCase())
                                )
                                .map((t) => (
                                  <button
                                    key={t.target_id}
                                    type="button"
                                    onClick={() => {
                                      updateRowField(row.id, "url", t.target_value);
                                      if (!row.label) updateRowField(row.id, "label", t.target_name);
                                      setShowTargetDropdown(prev => ({ ...prev, [row.id]: false }));
                                    }}
                                    className="w-full px-4 py-2 text-left hover:bg-slate-50 border-b border-gray-100 last:border-0"
                                  >
                                    <div className="text-sm font-medium text-slate-800">{t.target_name}</div>
                                    <div className="text-xs text-gray-500 truncate">{t.target_value}</div>
                                    {t.asset && (
                                      <div className="text-[10px] text-teal-600 font-medium">Asset: {t.asset.name}</div>
                                    )}
                                  </button>
                                ))}
                              {companyTargets.filter(t => 
                                t.target_name.toLowerCase().includes(row.url.toLowerCase()) || 
                                t.target_value.toLowerCase().includes(row.url.toLowerCase())
                              ).length === 0 && (
                                <div className="px-4 py-2 text-xs text-gray-500">No matching targets found. You can still enter a custom URL.</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Dynamic Entries List or Secure Note */}
                    <div className="col-span-1 md:col-span-12 mt-2">
                       {row.credential_type === "secure_note" ? (
                         <div>
                           <label className="block text-xs font-bold text-slate-700 mb-1.5">Secure Note Contents</label>
                           <textarea
                             value={row.notes}
                             onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateRowField(row.id, "notes", e.target.value)}
                             placeholder="Enter sensitive information here..."
                             rows={4}
                             className="w-full bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:bg-white outline-none transition-colors"
                           />
                         </div>
                       ) : (
                         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                           {row.entries.map((entry, idx) => (
                             <div key={idx} className="relative group/field bg-slate-50 border border-slate-100 rounded-lg p-3">
                               <div className="flex items-center justify-between mb-2">
                                 <select
                                   value={entry.type}
                                   onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateRowEntry(row.id, idx, "type", e.target.value)}
                                   className="text-[10px] bg-white border border-slate-200 rounded px-1 py-0.5 outline-none focus:border-green-500"
                                 >
                                   {entryTypes.map(t => (
                                     <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                                   ))}
                                 </select>
                                 <button
                                   onClick={() => removeRowEntry(row.id, idx)}
                                   disabled={row.entries.length === 1}
                                   className="opacity-0 group-hover/field:opacity-100 text-slate-400 hover:text-red-500 transition-all p-0.5"
                                 >
                                   ✕
                                 </button>
                               </div>

                               <div className="space-y-2">
                                 <input
                                   type="text"
                                   placeholder="Field Name"
                                   className="w-full bg-white border border-slate-200 rounded px-2 py-1 text-xs font-semibold outline-none focus:border-green-500"
                                   value={entry.key}
                                   onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRowEntry(row.id, idx, "key", e.target.value)}
                                 />
                                 
                                 <div className="relative">
                                   <input
                                     type={entry.type === "password" && !showValues[`${row.id}-${idx}`] ? "password" : "text"}
                                     placeholder={`Enter ${entry.key.toLowerCase() || 'value'}`}
                                     className="w-full bg-white border border-slate-200 rounded pl-2 pr-8 py-1.5 text-xs outline-none focus:border-green-500"
                                     value={entry.value}
                                     onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRowEntry(row.id, idx, "value", e.target.value)}
                                   />
                                   {entry.type === "password" && (
                                     <button
                                       type="button"
                                       onClick={() => toggleShowValue(`${row.id}-${idx}`)}
                                       className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-slate-600"
                                     >
                                       {showValues[`${row.id}-${idx}`] ? <FiEyeOff size={12} /> : <FiEye size={12} />}
                                     </button>
                                   )}
                                 </div>
                               </div>
                             </div>
                           ))}
                           
                           {/* Add Field Button */}
                           <button
                             onClick={() => addRowEntry(row.id)}
                             className="flex items-center justify-center gap-1.5 border-2 border-dashed border-slate-200 rounded-lg p-3 text-slate-400 hover:text-green-600 hover:border-green-200 hover:bg-green-50 transition-all text-xs font-medium"
                           >
                             <FiPlus size={14} /> Add Field
                           </button>
                         </div>
                       )}

                       {/* Row-level Notes (for all except secure_note which already has them as primary) */}
                       {row.credential_type !== "secure_note" && (
                         <div className="mt-4">
                           <button 
                             type="button"
                             onClick={() => {
                               // Toggle show notes - just an example, maybe just show it
                             }}
                             className="text-[10px] text-slate-400 font-medium hover:text-slate-600 mb-1"
                           >
                              Notes (optional)
                           </button>
                           <textarea
                             value={row.notes}
                             onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateRowField(row.id, "notes", e.target.value)}
                             placeholder="Additional notes about this credential..."
                             rows={1}
                             className="w-full bg-slate-50 border border-gray-100 rounded-lg px-3 py-1.5 text-xs focus:border-green-500 focus:bg-white outline-none transition-all resize-none"
                           />
                         </div>
                       )}
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-slate-200 flex justify-center">
            <button
              onClick={addRow}
              className="px-6 py-2.5 bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-800 border border-green-200 rounded-xl font-semibold flex items-center gap-2 transition-colors shadow-sm"
            >
              <FiPlus size={18} /> Add Another Credential
            </button>
          </div>

          {!isSubUserMode && !initialMasterPassword && (
             <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5 max-w-md">
               <h3 className="text-gray-900 font-semibold mb-2 flex items-center gap-2">
                 <FiLock className="text-green-600" /> Enter Master Password
               </h3>
               <p className="text-gray-600 text-xs mb-3">
                 Required to encrypt these credentials before saving.
               </p>
               <div className="relative">
                 <input
                   type={showPassword ? "text" : "password"}
                   value={masterPassword}
                   onChange={(e) => setMasterPassword(e.target.value)}
                   placeholder="Master Password"
                   className="w-full bg-white text-sm text-gray-900 rounded-lg px-3 py-2 pr-10 border border-green-300 focus:border-green-500 focus:ring-2 focus:ring-green-500 outline-none"
                 />
                 <button
                   type="button"
                   onClick={() => setShowPassword(!showPassword)}
                   className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                 >
                   {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                 </button>
               </div>
             </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-white rounded-b-2xl">
          <p className="text-xs text-gray-500 flex items-center gap-1.5 ml-2">
             <FiLock className="text-green-500" /> Data is encrypted locally before transmission.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAll}
              disabled={isLoading || (!isSubUserMode && !masterPassword)}
              className="px-6 py-2.5 bg-green-500 text-white text-sm font-semibold rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors shadow-sm flex items-center gap-2"
            >
              {isLoading ? "Encrypting & Saving..." : "Save All Credentials"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MultiCredentialModal;
